import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ref, get, set, remove, update, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../firebase/config';
import ListaDipendenti from './ListaDipendenti';
import ListaVeicoli from './ListaVeicoli';
import ListaClienti from './ListaClienti';
import ListaCantieri from './ListaCantieri';
import ProgrammiLavoro from './ProgrammiLavoro';
import RicercaProgrammi from './RicercaProgrammi';
import RicercaGiornaliere from './RicercaGiornaliere';
import RicercaBolle from './RicercaBolle';
import ReportGiornaliero from './ReportGiornaliero';
import CalendarioProgrammi from './CalendarioProgrammi';
import FatturazioneForm from './FatturazioneForm';
import ListaFatture from './ListaFatture';
import ReportFinanziario from './ReportFinanziario';
import CreaPreventivoForm from './CreaPreventivoForm';
import ListaPreventivi from './ListaPreventivi';
import '../styles/Dashboard.css';
import { toast } from 'react-hot-toast';


// ✅ NUOVO: Componente Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`Errore in ${this.props.sectionName}:`, error);
    console.error('Component stack:', errorInfo.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary" style={{
          padding: '20px',
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px',
          margin: '10px'
        }}>
          <h3>⚠️ Errore in {this.props.sectionName}</h3>
          <p style={{ color: '#666', fontSize: '14px' }}>{this.state.error?.message || 'Errore sconosciuto'}</p>
          <button
            onClick={this.handleRetry}
            className="btn btn-primary"
            style={{ marginTop: '10px' }}
          >
            🔄 Riprova
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function formattaData(offsetGiorni = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetGiorni);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

// ✅ PARSER ORE UNIFICATO: gestisce i formati generati automaticamente ("8h", "7h 30m")
// e quelli scritti a mano ("2,5h", "2.5h", "30m", "8:30", "2,5").
function calcolaOreDaDurata(val) {
  if (typeof val === 'number') return val;
  if (typeof val !== 'string') return 0;
  const v = val.trim();
  if (!v) return 0;

  // "8h", "2,5h", "7h30m", "30m"
  if (v.includes('h') || v.includes('m')) {
    const hMatch = v.match(/(\d+(?:[.,]\d+)?)h/);
    const mMatch = v.match(/(\d+(?:[.,]\d+)?)m/);
    const h = hMatch ? parseFloat(hMatch[1].replace(',', '.')) : 0;
    const m = mMatch ? parseFloat(mMatch[1].replace(',', '.')) : 0;
    return h + m / 60;
  }

  // "8:30"
  if (v.includes(':')) {
    const [h, m] = v.split(':').map(Number);
    return h + (m || 0) / 60;
  }

  // Numero puro "2,5" / "2.5"
  const parsed = parseFloat(v.replace(',', '.'));
  return isNaN(parsed) ? 0 : parsed;
}

// ✅ FIX (11/10/2026): formatta le ore totali arrotondando PRIMA i minuti totali,
// poi separando ore e minuti. Evita il "1h 60min" causato dagli errori di
// arrotondamento in virgola mobile (es. somma 20m + 1h40m = 1.999... ore).
function formattaOreMinuti(ore) {
  const totMin = Math.round((ore || 0) * 60);
  return `${Math.floor(totMin / 60)}h ${totMin % 60}min`;
}


// ==================== COMPONENTI FIGLI ====================

function DashboardHomeSection({ stats, statsLoading, riepilogo, riepilogoDomani, onNavigate, onBackupDownload, onBackupRestore }) {
  return (
    <div className="dashboard-home">
      <div className="stats-grid">
        {statsLoading ? (
          // ✅ LOADING STATE: mostra placeholder durante il caricamento
          [1, 2, 3, 4].map(i => (
            <div key={i} className="stat-card" style={{ opacity: 0.6, cursor: 'default' }}>
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <h3>Caricamento...</h3>
                <p className="stat-number">—</p>
              </div>
            </div>
          ))
        ) : (
          // ✅ DATI REALI: mostra le statistiche quando il caricamento è completo
          [
            { key: 'dipendenti', icon: '👷', label: 'Dipendenti', value: stats.dipendenti },
            { key: 'veicoli', icon: '🚚', label: 'Mezzi lavoro', value: stats.veicoli },
            { key: 'clienti', icon: '🏢', label: 'Clienti', value: stats.clienti },
            { key: 'cantieri', icon: '🏗️', label: 'Cantieri', value: stats.cantieri },
          ].map(item => (
            <div key={item.key} className="stat-card" onClick={() => onNavigate(item.key)} style={{ cursor: 'pointer' }}>
              <div className="stat-icon">{item.icon}</div>
              <div className="stat-content">
                <h3>{item.label}</h3>
                <p className="stat-number">{item.value}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="riepilogo-section">
        <h3 className="riepilogo-title">📆 Riepilogo di oggi ({formattaData(0)})</h3>
        {riepilogo.loading ? (
          <p className="loading-text">Caricamento...</p>
        ) : riepilogo.numProgrammi === 0 ? (
          <p className="empty-text">Nessun programma per oggi.</p>
        ) : (
          <div className="riepilogo-grid">
            <div className="riepilogo-card" onClick={() => onNavigate('calendario', 'agenda')} style={{ cursor: 'pointer' }}>
              <span className="riepilogo-icon">📅</span>
              <p className="riepilogo-number">{riepilogo.numProgrammi}</p>
              <p className="riepilogo-label">Programmi oggi</p>
            </div>
            <div className="riepilogo-card">
              <span className="riepilogo-icon">⏱️</span>
              <p className="riepilogo-number">
                {formattaOreMinuti(riepilogo.oreTotali)}
              </p>
              <p className="riepilogo-label">Ore totali</p>
            </div>
            <div className="riepilogo-card">
              <span className="riepilogo-icon">🏗️</span>
              <p className="riepilogo-number">{riepilogo.cantieriCoinvolti}</p>
              <p className="riepilogo-label">Cantieri attivi</p>
            </div>
            <div className="riepilogo-card">
              <span className="riepilogo-icon">🚚</span>
              <p className="riepilogo-number">{riepilogo.veicoliCoinvolti}</p>
              <p className="riepilogo-label">Mezzi lavoro impiegati</p>
            </div>
          </div>
        )}
      </div>

      <div className="riepilogo-section">
        <h3 className="riepilogo-title">📆 Riepilogo di domani ({formattaData(1)})</h3>
        {riepilogoDomani.loading ? (
          <p className="loading-text">Caricamento...</p>
        ) : riepilogoDomani.numProgrammi === 0 ? (
          <p className="empty-text">Nessun programma per domani.</p>
        ) : (
          <div className="riepilogo-grid">
            <div className="riepilogo-card">
              <span className="riepilogo-icon">📅</span>
              <p className="riepilogo-number">{riepilogoDomani.numProgrammi}</p>
              <p className="riepilogo-label">Programmi domani</p>
            </div>
            <div className="riepilogo-card">
              <span className="riepilogo-icon">⏱️</span>
              <p className="riepilogo-number">
                {formattaOreMinuti(riepilogoDomani.oreTotali)}
              </p>
              <p className="riepilogo-label">Ore totali</p>
            </div>
            <div className="riepilogo-card">
              <span className="riepilogo-icon">🏗️</span>
              <p className="riepilogo-number">{riepilogoDomani.cantieriCoinvolti}</p>
              <p className="riepilogo-label">Cantieri attivi</p>
            </div>
            <div className="riepilogo-card">
              <span className="riepilogo-icon">🚚</span>
              <p className="riepilogo-number">{riepilogoDomani.veicoliCoinvolti}</p>
              <p className="riepilogo-label">Mezzi lavoro impiegati</p>
            </div>
          </div>
        )}
      </div>

      <div className="backup-section">
        <h2 className="backup-title">💾 Sistema di Backup</h2>
        <p className="backup-desc">Proteggi i tuoi dati con backup e ripristino completo</p>
        <div className="backup-actions">
          <button className="btn btn-primary" onClick={onBackupDownload}>📥 Scarica Backup</button>
          <button className="btn btn-outline" onClick={onBackupRestore}>📤 Ripristina Backup</button>
        </div>
        <div className="backup-info">
          <strong>ℹ️ Informazioni:</strong>
          <ul>
            <li>Il backup include tutti i dati (dipendenti, veicoli, cantieri, clienti, programmi)</li>
            <li>Nome file formato: <code>crg_GGMMAA_HHMM.json</code></li>
            <li>Il ripristino sostituisce i dati delle sezioni presenti nel file di backup (le sezioni non incluse non vengono toccate)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function LogErroriSection({ logs, loading, filters, setFilters, onRefresh, onExport, onClear }) {
  const severityColor = { critical: '#dc3545', warning: '#fd7e14', info: '#0d6efd' };
  const tipoColor = { Giornaliera: '#667eea', Bolla: '#fd7e14', Login: '#28a745', Sync: '#6f42c1' };

  const logsFiltrati = logs.filter(l => {
    const search = filters.search.toLowerCase();
    if (search && !`${l.dipendente} ${l.errore} ${l.dettaglio} ${l.tipo}`.toLowerCase().includes(search)) return false;
    if (filters.tipo && l.tipo !== filters.tipo) return false;
    if (filters.severity && l.severity !== filters.severity) return false;
    return true;
  });

  const resetFilters = () => setFilters({ search: '', tipo: '', severity: '' });

  return (
    <div className="section-content">
      <div className="section-header">
        <h2>🔴 Log Errori Automatici</h2>
        <div className="section-actions">
          <button className="btn btn-secondary" onClick={onRefresh}>🔄 Aggiorna</button>
          <button className="btn btn-success" onClick={() => onExport(logsFiltrati)} disabled={logsFiltrati.length === 0}>⬇️ Esporta CSV</button>
          <button className="btn btn-danger" onClick={onClear} disabled={logs.length === 0}>🗑️ Svuota Log</button>
        </div>
      </div>

      <div className="stats-quick-grid">
        {[
          { label: 'Totale', value: logs.length, color: '#667eea' },
          { label: 'Critici', value: logs.filter(l => l.severity === 'critical').length, color: '#dc3545' },
          { label: 'Giornaliere', value: logs.filter(l => l.tipo === 'Giornaliera').length, color: '#667eea' },
          { label: 'Bolle', value: logs.filter(l => l.tipo === 'Bolla').length, color: '#fd7e14' },
        ].map(s => (
          <div key={s.label} className="stat-mini-card">
            <div className="stat-mini-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-mini-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="🔍 Cerca dipendente, errore..."
          value={filters.search}
          onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
          className="input-filter"
        />
        <select value={filters.tipo} onChange={e => setFilters(prev => ({ ...prev, tipo: e.target.value }))} className="select-filter">
          <option value="">Tutti i tipi</option>
          <option value="Giornaliera">Giornaliera</option>
          <option value="Bolla">Bolla</option>
          <option value="Login">Login</option>
          <option value="Sync">Sync</option>
        </select>
        <select value={filters.severity} onChange={e => setFilters(prev => ({ ...prev, severity: e.target.value }))} className="select-filter">
          <option value="">Tutte le severità</option>
          <option value="critical">Critico</option>
          <option value="warning">Avviso</option>
          <option value="info">Info</option>
        </select>
        <button className="btn btn-light" onClick={resetFilters}>✕ Reset</button>
      </div>

      {loading ? (
        <p className="loading-text">⏳ Caricamento log...</p>
      ) : logsFiltrati.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <div className="empty-title">Nessun errore trovato</div>
          <div className="empty-subtitle">Prova a modificare i filtri oppure clicca Aggiorna</div>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                {['Data','Ora','Dipendente','Tipo','Codice Errore','Dettaglio','Versione','Severità'].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logsFiltrati.map((log, i) => (
                <tr key={log.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                  <td className="cell-mono">{log.data || '—'}</td>
                  <td className="cell-mono cell-muted">{log.ora || '—'}</td>
                  <td className="cell-bold">{log.dipendente || '—'}</td>
                  <td>
                    <span className="badge" style={{ background: (tipoColor[log.tipo] || '#888') + '20', color: tipoColor[log.tipo] || '#666' }}>
                      {log.tipo || '—'}
                    </span>
                  </td>
                  <td className="cell-mono cell-error">{log.errore || '—'}</td>
                  <td className="cell-ellipsis" title={log.dettaglio}>{log.dettaglio || '—'}</td>
                  <td className="cell-mono cell-primary">{log.appVersion || '—'}</td>
                  <td>
                    <span className="badge" style={{ background: (severityColor[log.severity] || '#888') + '20', color: severityColor[log.severity] || '#888' }}>
                      {log.severity === 'critical' ? 'Critico' : log.severity === 'warning' ? 'Avviso' : 'Info'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="table-footer">{logsFiltrati.length} {logsFiltrati.length === 1 ? 'riga' : 'righe'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== COMPONENTE PRINCIPALE ====================


function FatturePreventiviSection({ onNavigate }) {
  return (
    <div className="lista-container">
      <div className="lista-header">
        <h2>💰 Fatture / Preventivi</h2>
      </div>

      <div className="fatture-preventivi-actions">

	<button
  	className="fatture-preventivi-button"
  	onClick={() => onNavigate('creaPreventivo')}
	>
  	<span className="fatture-preventivi-icon">📝</span>
  	<span>Crea preventivo</span>
	</button>


        <button
          className="fatture-preventivi-button"
          onClick={() => onNavigate('listaPreventivi')}
        >
          <span className="fatture-preventivi-icon">📋</span>
          <span>Lista preventivi</span>
        </button>

        <button
          className="fatture-preventivi-button"
          onClick={() => onNavigate('creaFattura')}
        >
          <span className="fatture-preventivi-icon">🧾</span>
          <span>Crea fattura</span>
        </button>

        <button
          className="fatture-preventivi-button"
          onClick={() => onNavigate('listaFatture')}
        >
          <span className="fatture-preventivi-icon">📋</span>
          <span>Lista fatture</span>
        </button>

        <button
          className="fatture-preventivi-button"
          onClick={() => onNavigate('reportFinanziario')}
        >
          <span className="fatture-preventivi-icon">📊</span>
          <span>Report finanziario</span>
        </button>
      </div>
    </div>
  );
}


async function calcolaRiepilogoGiorno(offsetGiorni) {
  const d = new Date();
  d.setDate(d.getDate() + offsetGiorni);
  const data = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const snap = await get(query(ref(database, 'programmi-lavorativi'), orderByChild('data'), equalTo(data)));
  if (!snap.exists()) {
    return { numProgrammi: 0, oreTotali: 0, cantieriCoinvolti: 0, veicoliCoinvolti: 0, loading: false };
  }

  const lista = Object.values(snap.val());
  const oreTotali = lista.reduce((tot, p) => tot + calcolaOreDaDurata(p.durataLavoro), 0);

  return {
    numProgrammi: lista.length,
    oreTotali,
    cantieriCoinvolti: new Set(lista.map(p => p.cantiereId).filter(Boolean)).size,
    veicoliCoinvolti: new Set(lista.map(p => p.veicoloId).filter(Boolean)).size,
    loading: false,
  };
}


function AdminDashboard() {
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [vistaInizioCalendario, setVistaInizioCalendario] = useState('month');

  const [stats, setStats] = useState({ dipendenti: 0, veicoli: 0, clienti: 0, cantieri: 0 });
  const [statsLoading, setStatsLoading] = useState(true); 
  const [riepilogoOggi, setRiepilogoOggi] = useState({
    numProgrammi: 0, oreTotali: 0, cantieriCoinvolti: 0, veicoliCoinvolti: 0, loading: true,
  });

  const [riepilogoDomani, setRiepilogoDomani] = useState({
    numProgrammi: 0, oreTotali: 0, cantieriCoinvolti: 0, veicoliCoinvolti: 0, loading: true,
  });

  const [errorLogs, setErrorLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logNonVisti, setLogNonVisti] = useState(0);
  const [logFilters, setLogFilters] = useState({ search: '', tipo: '', severity: '' });

// ✅ AGGIUNGI QUI IL NUOVO USEEFFECT DI CLEANUP
useEffect(() => {
  isMountedRef.current = true;
  return () => {
    isMountedRef.current = false;
  };
}, []);

  // Carica statistiche e riepilogo quando si torna alla home
  useEffect(() => {
    if (activeSection === 'home') {
      caricaStatistiche();
      caricaRiepilogoOggi();
      caricaRiepilogoDomani();
    }
  }, [activeSection]);

  useEffect(() => {
    caricaErrorLogs();
  }, []);

const caricaStatistiche = useCallback(async () => {
  setStatsLoading(true);
  try {
    const [dipSnap, veicSnap, cliSnap, cantSnap] = await Promise.all([
      get(ref(database, 'dipendenti')),
      get(ref(database, 'veicoli')),
      get(ref(database, 'clienti')),
      get(ref(database, 'cantieri'))
    ]);

    if (!isMountedRef.current) return;

    setStats({
      dipendenti: dipSnap.exists() ? Object.keys(dipSnap.val()).length : 0,
      veicoli: veicSnap.exists() ? Object.keys(veicSnap.val()).length : 0,
      clienti: cliSnap.exists() ? Object.keys(cliSnap.val()).length : 0,
      cantieri: cantSnap.exists() ? Object.keys(cantSnap.val()).length : 0
    });
  } catch (error) {
    if (!isMountedRef.current) return;
    console.error('Errore caricamento statistiche:', error);
  } finally {
    setStatsLoading(false);
  }
}, [database]);


  const caricaRiepilogoOggi = useCallback(async () => {
    try {
      setRiepilogoOggi(prev => ({ ...prev, loading: true }));
      const oggi = new Date();
      const dataOggi = `${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}-${String(oggi.getDate()).padStart(2, '0')}`;

      // ✅ OTTIMIZZAZIONE: Query server-side invece di scaricare tutto il DB
      const snap = await get(query(ref(database, 'programmi-lavorativi'), orderByChild('data'), equalTo(dataOggi)));
      
      // ✅ CONTROLLO: aggiorna solo se ancora montato
      if (!isMountedRef.current) return;

      if (!snap.exists()) {
        setRiepilogoOggi({ numProgrammi: 0, oreTotali: 0, cantieriCoinvolti: 0, veicoliCoinvolti: 0, loading: false });
        return;
      }

      const diOggi = Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }));
      const oreTotali = diOggi.reduce((tot, p) => tot + calcolaOreDaDurata(p.durataLavoro), 0);

    if (!isMountedRef.current) return;

    setRiepilogoOggi({
      numProgrammi: diOggi.length,
      oreTotali: oreTotali,
      cantieriCoinvolti: new Set(diOggi.map(p => p.cantiereId).filter(Boolean)).size,
      veicoliCoinvolti: new Set(diOggi.map(p => p.veicoloId).filter(Boolean)).size,
      loading: false,
    });
  } catch (e) {
    if (!isMountedRef.current) return;
    console.error('Errore riepilogo oggi:', e);
    setRiepilogoOggi(prev => ({ ...prev, loading: false }));
  }
}, [database]);


  const caricaRiepilogoDomani = useCallback(async () => {
    try {
      setRiepilogoDomani(prev => ({ ...prev, loading: true }));
      const risultato = await calcolaRiepilogoGiorno(1);
      if (!isMountedRef.current) return;
      setRiepilogoDomani(risultato);
    } catch (e) {
      if (!isMountedRef.current) return;
      console.error('Errore riepilogo domani:', e);
      setRiepilogoDomani(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch {
      toast.error('Errore durante il logout', { duration: 5000 });
    }
  };

const caricaErrorLogs = useCallback(async () => {
  setLogsLoading(true);
  try {
    const snap = await get(ref(database, 'error_logs'));
    
    // ✅ CONTROLLO
    if (!isMountedRef.current) return;

    if (snap.exists()) {
      const raw = snap.val();
      const lista = Object.entries(raw)
        .map(([id, v]) => ({ id, ...v }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setErrorLogs(lista);
      setLogNonVisti(lista.filter(l => !l.visto).length);
    } else {
      setErrorLogs([]);
      setLogNonVisti(0);
    }
  } catch (err) {
    if (!isMountedRef.current) return;
    console.error('Errore caricamento log:', err.message);
  } finally {
    if (!isMountedRef.current) return;
    setLogsLoading(false);
  }
}, [database]);



  const marcaLogComeVisti = useCallback(async () => {
    try {
      const snap = await get(ref(database, 'error_logs'));
      if (!snap.exists()) return;
      const raw = snap.val();
      const updates = {};
      Object.entries(raw).forEach(([id, v]) => {
        if (!v.visto) updates[`error_logs/${id}/visto`] = true;
      });
      if (Object.keys(updates).length > 0) {
        // ✅ FIX: Uso diretto di update già importato
        await update(ref(database, '/'), updates);
      }
      setLogNonVisti(0);
    } catch (err) {
      console.warn('Errore marca visti:', err.message);
    }
  }, [database]);


  const cancellaErrorLogs = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare tutti i log errori? Operazione irreversibile.')) return;
    try {
      await set(ref(database, 'error_logs'), null);
      setErrorLogs([]);
      toast.success('Log errori eliminati');
    } catch (err) {
      toast.error('Errore: ' + err.message, { duration: 5000 });
    }
  };

const esportaLogCSV = (logs) => {
  // Funzione di escaping robusta per CSV
  const escapeCsv = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    // Se contiene virgola, doppi apici o newline → racchiudi e escapa
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const headers = ['Data','Ora','Dipendente','Tipo','Errore','Dettaglio','Severity','NumeroBolla','ClienteId','CantiereId'];
  
  const rows = logs.map(l => [
    escapeCsv(l.data),
    escapeCsv(l.ora),
    escapeCsv(l.dipendente),
    escapeCsv(l.tipo),
    escapeCsv(l.errore),
    escapeCsv(l.dettaglio),
    escapeCsv(l.severity),
    escapeCsv(l.numeroBolla),
    escapeCsv(l.clienteId),
    escapeCsv(l.cantiereId)
  ].join(','));

  const csv = [headers.join(','), ...rows].join('\n');
  
  // Aggiungi BOM per compatibilità Excel con caratteri speciali (es. ì, à, è)
  const bom = '\uFEFF';
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Data in fuso orario di Roma (non UTC): nelle prime ore del mattino (00:00-03:00)
  // il file si chiama con il giorno corrente invece del giorno precedente
  const dataFile = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
  a.download = `log_errori_${dataFile}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};


const NODI_BACKUP = [
  'dipendenti', 'veicoli', 'clienti', 'cantieri',
  'programmi-lavorativi', 'giornaliera', 'bolle', 'bolleForm',
  'bolle_contatore', 'fotoCantiere', 'fatture', 'preventivi',
  'users', 'error_logs'
];

const scaricaBackup = async () => {
  try {
    const risultati = await Promise.all(
      NODI_BACKUP.map(async (nodo) => {
        try {
          const snap = await get(ref(database, nodo));
          return { nodo, ok: true, val: snap.exists() ? snap.val() : null };
        } catch (e) {
          return { nodo, ok: false, errore: e.message };
        }
      })
    );

const falliti = risultati.filter(r => !r.ok);
if (falliti.length > 0) {
  const dettagli = falliti.map(f => `${f.nodo} (${f.errore})`).join('\n');
  toast.error(`Backup NON completo.\nNodi non leggibili:\n${dettagli}`, { duration: 10000 });
  
  // Log dettagliato in console per debug
  console.error('Dettagli backup fallito:', falliti);
  return;
}


    const data = {};
    risultati.forEach(r => { if (r.val !== null) data[r.nodo] = r.val; });

    const backupData = {
      version: '2.0',
      timestamp: new Date().toISOString(),
      nodi: Object.keys(data),
      data
    };

    const oggi = new Date();
    const nomeFile = `crg_${String(oggi.getDate()).padStart(2, '0')}${String(oggi.getMonth() + 1).padStart(2, '0')}${String(oggi.getFullYear()).slice(-2)}_${String(oggi.getHours()).padStart(2, '0')}${String(oggi.getMinutes()).padStart(2, '0')}.json`;

    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Backup scaricato: ${nomeFile} (${Object.keys(data).length} nodi)`);
  } catch (error) {
    toast.error('Errore durante il backup: ' + error.message, { duration: 5000 });
  }
};

  const selezionaFileBackup = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.warning('Seleziona un file JSON valido!', { duration: 3000 });
        return;
      }
      setBackupFile(file);
    }
  };

  const ripristinaBackup = async () => {
    if (!backupFile) {
      toast.warning('Seleziona prima un file di backup!', { duration: 3000 });
      return;
    }
    if (!window.confirm('ATTENZIONE! I dati presenti nel file di backup sostituiranno gli attuali (le sezioni non incluse nel file resteranno invariate). Sei sicuro di voler continuare?')) return;

    setIsRestoring(true);
    try {
const fileContent = await backupFile.text();
const backupData = JSON.parse(fileContent);

// ✅ VALIDAZIONE VERSIONE BACKUP
const SUPPORTED_VERSIONS = ['1.0', '2.0'];
if (!backupData.version) {
  throw new Error('File di backup non valido: manca il campo versione');
}
if (!SUPPORTED_VERSIONS.includes(backupData.version)) {
  throw new Error(`Versione backup non supportata: ${backupData.version}. Versioni compatibili: ${SUPPORTED_VERSIONS.join(', ')}`);
}

if (!backupData.data) throw new Error('File di backup non valido: manca il campo data');


      // ✅ Fix T1 (30/09/2026): ripristino retro-compatibile — tocca SOLO le sezioni presenti nel file,
      // così un backup vecchio (senza preventivi/fatture) non cancella i dati attuali di quelle sezioni.
      const updates = {};
      for (const sezione of Object.keys(backupData.data)) {
        const data = backupData.data[sezione];
        updates[sezione] = (data !== null && data !== undefined &&
  !(typeof data === 'object' && Object.keys(data).length === 0)) ? data : null;
      }

      // ✅ OTTIMIZZAZIONE: Aggiornamento atomico in un'unica chiamata

      await update(ref(database, '/'), updates);
      toast.success('Backup ripristinato con successo!', { duration: 5000 });
      caricaStatistiche();
      if (activeSection === 'home') {
        caricaRiepilogoOggi();
        caricaRiepilogoDomani();
      }
      if (activeSection === 'log-errori') {
        caricaErrorLogs();
      }

    } catch (error) {
      toast.error('Errore durante il ripristino: ' + error.message, { duration: 8000 });
      console.error(error);
    } finally {
      setIsRestoring(false);
      setBackupFile(null);
      setShowBackupModal(false);
    }
  };

const handleNavigate = useCallback((section, vista = null) => {
  setActiveSection(section);
  setMenuOpen(false);
  if (vista) setVistaInizioCalendario(vista);
}, []);


  return (
    <div className="admin-container">
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2><img src="/CRG_logo.png" alt="crg" className="sidebar-logo" /></h2>
        </div>
        <div className="welcome-section">
          <img src="/jody-admin.png" alt="admin" className="admin-avatar" />
          <p><b>Admin</b> ({currentUser?.email})</p>
        </div>
        <div>
          <p className="app-version">v{process.env.REACT_APP_VERSION || '—'}</p>
        </div>

<nav className="sidebar-nav">
  {[
    { id: 'home', icon: '🏠', label: 'Home' },
    { id: 'dipendenti', icon: '👷', label: 'Dipendenti' },
    { id: 'veicoli', icon: '🚚', label: 'Mezzi lavoro' },
    { id: 'clienti', icon: '🏢', label: 'Clienti' },
    { id: 'cantieri', icon: '🏗️', label: 'Cantieri' },
    { id: 'programmi', icon: '📋', label: 'Programmi' },
    { id: 'calendario', icon: '📅', label: 'Calendario' },
    { id: 'ricerca', icon: '🔍', label: 'Ricerca' },
    { id: 'ricercaGiornaliere', icon: '🔍', label: 'Giornaliere' },
    { id: 'ricercaBolle', icon: '🧾', label: 'Bolle' },
    { id: 'fatturePreventivi', icon: '💰', label: 'Fatture / Preventivi', speciale: true },
    { id: 'report', icon: '📊', label: 'Report' },
  ].map(item => (
    <button
      key={item.id}
      className={`nav-item ${activeSection === item.id ? 'active' : ''} ${item.speciale ? 'fatture-item' : ''}`}
      onClick={() => handleNavigate(item.id)}
    >
      {item.icon} {item.label}
    </button>
  ))}

  <button
    className={`nav-item ${activeSection === 'log-errori' ? 'active' : ''}`}
    onClick={() => {
      handleNavigate('log-errori');
      caricaErrorLogs();
      marcaLogComeVisti();
    }}
  >
    🔴 Log Errori
    {logNonVisti > 0 && (
      <span className="badge-notification">
        {logNonVisti > 99 ? '99+' : logNonVisti}
      </span>
    )}
  </button>

  <div className="sidebar-footer">
    <button className="logout-btn" onClick={handleLogout}>
      ESCI
    </button>
  </div>
</nav>
      </aside>

      <button className="mobile-menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>☰</button>

<main className="main-content">
  {activeSection === 'home' && (
    <ErrorBoundary sectionName="Home">
<DashboardHomeSection
  stats={stats}
  statsLoading={statsLoading}  // ← AGGIUNTO
  riepilogo={riepilogoOggi}
  riepilogoDomani={riepilogoDomani}
  onNavigate={handleNavigate}
  onBackupDownload={scaricaBackup}
  onBackupRestore={() => setShowBackupModal(true)}
/>
    </ErrorBoundary>
  )}

  {activeSection === 'dipendenti' && (
    <ErrorBoundary sectionName="Dipendenti">
      <ListaDipendenti />
    </ErrorBoundary>
  )}

  {activeSection === 'veicoli' && (
    <ErrorBoundary sectionName="Veicoli">
      <ListaVeicoli />
    </ErrorBoundary>
  )}

  {activeSection === 'clienti' && (
    <ErrorBoundary sectionName="Clienti">
      <ListaClienti />
    </ErrorBoundary>
  )}

  {activeSection === 'cantieri' && (
    <ErrorBoundary sectionName="Cantieri">
      <ListaCantieri />
    </ErrorBoundary>
  )}

  {activeSection === 'programmi' && (
    <ErrorBoundary sectionName="Programmi Lavoro">
      <ProgrammiLavoro />
    </ErrorBoundary>
  )}

  {activeSection === 'ricerca' && (
    <ErrorBoundary sectionName="Ricerca Programmi">
      <RicercaProgrammi />
    </ErrorBoundary>
  )}

  {activeSection === 'ricercaGiornaliere' && (
    <ErrorBoundary sectionName="Ricerca Giornaliere">
      <RicercaGiornaliere />
    </ErrorBoundary>
  )}

  {activeSection === 'ricercaBolle' && (
    <ErrorBoundary sectionName="Ricerca Bolle">
      <RicercaBolle />
    </ErrorBoundary>
  )}

  {activeSection === 'fatturePreventivi' && (
    <ErrorBoundary sectionName="Fatture/Preventivi">
      <FatturePreventiviSection onNavigate={handleNavigate} />
    </ErrorBoundary>
  )}

  {activeSection === 'creaPreventivo' && (
    <ErrorBoundary sectionName="Crea Preventivo">
      <CreaPreventivoForm onBack={() => setActiveSection('fatturePreventivi')} />
    </ErrorBoundary>
  )}

  {activeSection === 'listaPreventivi' && (
    <ErrorBoundary sectionName="Lista Preventivi">
      <ListaPreventivi onBack={() => setActiveSection('fatturePreventivi')} />
    </ErrorBoundary>
  )}

  {activeSection === 'creaFattura' && (
    <ErrorBoundary sectionName="Crea Fattura">
      <FatturazioneForm onBack={() => setActiveSection('fatturePreventivi')} />
    </ErrorBoundary>
  )}

  {activeSection === 'listaFatture' && (
    <ErrorBoundary sectionName="Lista Fatture">
      <ListaFatture onBack={() => setActiveSection('fatturePreventivi')} />
    </ErrorBoundary>
  )}

  {activeSection === 'reportFinanziario' && (
    <ErrorBoundary sectionName="Report Finanziario">
      <ReportFinanziario onBack={() => setActiveSection('fatturePreventivi')} />
    </ErrorBoundary>
  )}

  {activeSection === 'report' && (
    <ErrorBoundary sectionName="Report Giornaliero">
      <ReportGiornaliero />
    </ErrorBoundary>
  )}

  {activeSection === 'calendario' && (
    <ErrorBoundary sectionName="Calendario">
      <CalendarioProgrammi
        vistaIniziale={vistaInizioCalendario}
        onVistaCambiata={() => setVistaInizioCalendario('month')}
      />
    </ErrorBoundary>
  )}

  {activeSection === 'log-errori' && (
    <ErrorBoundary sectionName="Log Errori">
      <LogErroriSection
        logs={errorLogs}
        loading={logsLoading}
        filters={logFilters}
        setFilters={setLogFilters}
        onRefresh={caricaErrorLogs}
        onExport={esportaLogCSV}
        onClear={cancellaErrorLogs}
      />
    </ErrorBoundary>
  )}
</main>


      {showBackupModal && (
        <div className="modal-overlay" onClick={() => !isRestoring && setShowBackupModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">📤 Ripristina Backup</h3>
            <div className="alert-warning">
              <strong>⚠️ ATTENZIONE:</strong>
              <p>Il ripristino sostituirà i dati attuali <strong>solo per le sezioni presenti nel file di backup</strong>. Le sezioni non incluse nel file resteranno invariate. Questa operazione non può essere annullata.</p>
            </div>
            <div className="form-group">
              <label className="form-label">Seleziona file di backup (crg_*.json):</label>
              <input
                type="file"
                accept=".json"
                onChange={selezionaFileBackup}
                disabled={isRestoring}
                className="file-input"
              />
              {backupFile && <p className="file-selected">✅ File selezionato: {backupFile.name}</p>}
            </div>
            <div className="modal-buttons">
              <button className="btn btn-light" onClick={() => setShowBackupModal(false)} disabled={isRestoring}>Annulla</button>
              <button
                className="btn btn-success"
                onClick={ripristinaBackup}
                disabled={!backupFile || isRestoring}
              >
                {isRestoring ? '⏳ Ripristino in corso...' : '🔄 Ripristina Dati'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
